/**
 * Coordinate conversion utilities
 * Handles conversions between different coordinate systems used in the application
 *
 * Uses Mapbox's unproject method for reliable screen-to-world conversion,
 * which is the community-recommended approach for Mapbox + Three.js interaction.
 *
 * @see C:\Projects\AV8\PlaneMAX\apps\demo\src\utils\coordinates.ts
 */

import mapboxgl from 'mapbox-gl'
import * as THREE from 'three'

/**
 * Convert screen coordinates to world position using Mapbox's unproject method
 * This is the community-recommended approach for Mapbox + Three.js interaction
 *
 * Why this works better than Three.js raycasting:
 * - Uses Mapbox's own coordinate system and projection
 * - Doesn't require camera matrix synchronization
 * - Doesn't depend on an invisible ground plane
 * - Works regardless of Three.js camera configuration
 *
 * @param mouse Normalized device coordinates (-1 to 1)
 * @param map Mapbox map instance
 * @param modelOrigin Model origin in [lng, lat]
 * @returns World position in meters, or null if conversion fails
 */
export function screenToWorldPosition(
  mouse: THREE.Vector2,
  map: mapboxgl.Map,
  modelOrigin: [number, number]
): THREE.Vector3 | null {
  try {
    const canvas = map.getCanvas()
    const rect = canvas.getBoundingClientRect()

    // Convert normalized device coordinates back to pixel coordinates
    const x = ((mouse.x + 1) / 2) * rect.width
    const y = ((1 - mouse.y) / 2) * rect.height

    // Use Mapbox's unproject to get lng/lat at this screen position
    const lngLat = map.unproject([x, y])

    // Convert to Mercator coordinates
    const mercator = mapboxgl.MercatorCoordinate.fromLngLat(lngLat, 0)

    // Get the model's origin in Mercator coordinates
    const modelMercator = mapboxgl.MercatorCoordinate.fromLngLat(modelOrigin, 0)

    // Calculate offset from model origin in Mercator units
    const offsetX = mercator.x - modelMercator.x
    const offsetY = mercator.y - modelMercator.y

    // Convert to meters (our scene units)
    const scale = mercator.meterInMercatorCoordinateUnits()
    const worldX = offsetX / scale
    const worldZ = offsetY / scale // Scene +Z = south aligns with Mercator +Y = south

    return new THREE.Vector3(worldX, 0, worldZ)
  } catch (error) {
    console.error('Screen to world position conversion error:', error)
    return null
  }
}

/**
 * Convert mouse event to normalized device coordinates
 *
 * @param event Mouse event
 * @param canvas Canvas element
 * @returns Normalized coordinates (-1 to 1)
 */
export function mouseEventToNDC(event: MouseEvent, canvas: HTMLCanvasElement): THREE.Vector2 {
  const rect = canvas.getBoundingClientRect()
  const x = ((event.clientX - rect.left) / rect.width) * 2 - 1
  const y = -((event.clientY - rect.top) / rect.height) * 2 + 1
  return new THREE.Vector2(x, y)
}
